import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { orgContext } from '@/core/api';
import type { ApiError } from '@/core/api';
import { toSessionOrg } from '@/features/auth/sessionOrg';
import { useSession } from '@/stores/session';
import { onboardOrganization } from '../api';

const schema = z.object({
  name: z.string().trim().min(1, 'Workspace name is required').max(255),
  base_currency: z.enum(['USD', 'EUR', 'GBP', 'AUD']),
});
type FormValues = z.infer<typeof schema>;

const CURRENCIES = [
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'GBP', label: 'GBP — British Pound' },
  { value: 'AUD', label: 'AUD — Australian Dollar' },
] as const;

type Step = 0 | 1 | 2;
const STEP_TITLES = ['Workspace name', 'Base currency', 'Confirm'];

/**
 * 3-step onboarding wizard. The backend already accepts a single POST so this
 * is purely UX scaffolding — we collect the data over steps and submit once.
 * On success we push the new org into the session store and route to /.
 */
export function OnboardPage() {
  const navigate = useNavigate();
  const setSession = useSession((s) => s.setSession);
  const currentUser = useSession((s) => s.user);
  const role = useSession((s) => s.role);
  const orgs = useSession((s) => s.organizations);

  const [step, setStep] = useState<Step>(0);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', base_currency: 'USD' },
    mode: 'onChange',
  });

  const submit = useMutation({
    mutationFn: (input: FormValues) => onboardOrganization(input),
    onSuccess: (org) => {
      // Switch the active tenant + refresh session-store list optimistically.
      orgContext.set(org.slug);
      setSession({
        user: currentUser ?? { id: 0, username: '', email: '', first_name: '', last_name: '', role: '' },
        // Founder is granted by the backend's onboarding service; mirror it
        // here for the UI gate (the next backend call would reaffirm it).
        role: 'founder',
        organizations: [...orgs, toSessionOrg(org)],
        activeOrgSlug: org.slug,
        whiteLabel: false,
      });
      toast.success(`Workspace "${org.name}" created`);
      navigate('/');
    },
    onError: (err: ApiError) => {
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          form.setError(field as keyof FormValues, { message: messages.join(' ') });
        }
        // Surface field errors on the step that owns them.
        if (err.fieldErrors.name) setStep(0);
        else if (err.fieldErrors.base_currency) setStep(1);
      } else {
        toast.error(err.message);
      }
    },
  });

  async function next() {
    if (step === 0) {
      const ok = await form.trigger('name');
      if (ok) setStep(1);
    } else if (step === 1) {
      const ok = await form.trigger('base_currency');
      if (ok) setStep(2);
    } else {
      const ok = await form.trigger();
      if (ok) submit.mutate(form.getValues());
    }
  }

  function back() {
    if (step > 0) setStep((step - 1) as Step);
  }

  const values = form.getValues();
  const role_OK = role; // suppress unused-var warning if any
  void role_OK;

  return (
    <div className="bg-muted/30 flex min-h-screen items-center justify-center p-6">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-medium tracking-wide uppercase">
            <Sparkles className="size-3.5" />
            Step {step + 1} / 3 · {STEP_TITLES[step]}
          </div>
          <CardTitle className="text-2xl">Create your workspace</CardTitle>
          <CardDescription>
            A workspace is your organization. You become the Super Admin + Founder.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={(e) => e.preventDefault()} className="space-y-6">
              {step === 0 ? (
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Workspace name</FormLabel>
                      <FormControl>
                        <Input placeholder="Acme Agency" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}

              {step === 1 ? (
                <FormField
                  control={form.control}
                  name="base_currency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Base / reporting currency</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {CURRENCIES.map((c) => (
                            <SelectItem key={c.value} value={c.value}>
                              {c.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                      <p className="text-muted-foreground text-xs">
                        Reports roll up to this currency. Fixed after creation.
                      </p>
                    </FormItem>
                  )}
                />
              ) : null}

              {step === 2 ? (
                <dl className="grid grid-cols-3 gap-x-3 gap-y-2 text-sm">
                  <dt className="text-muted-foreground col-span-1">Workspace</dt>
                  <dd className="col-span-2 font-medium">{values.name}</dd>
                  <dt className="text-muted-foreground col-span-1">Base currency</dt>
                  <dd className="col-span-2 font-medium tabular-nums">{values.base_currency}</dd>
                  <dt className="text-muted-foreground col-span-1">You</dt>
                  <dd className="col-span-2 font-medium">
                    {currentUser?.email ?? '—'} (Super Admin + Founder)
                  </dd>
                </dl>
              ) : null}

              <div className="flex justify-between gap-2 pt-2">
                <Button type="button" variant="outline" onClick={back} disabled={step === 0}>
                  <ArrowLeft className="size-4" /> Back
                </Button>
                <Button type="button" onClick={next} disabled={submit.isPending}>
                  {step < 2 ? (
                    <>
                      Next <ArrowRight className="size-4" />
                    </>
                  ) : submit.isPending ? (
                    'Creating…'
                  ) : (
                    <>
                      <Check className="size-4" /> Create workspace
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
